/**
 * @fileoverview Authentication context for the web runtime.
 *
 * Auth is optional. When Supabase is not configured the provider reports
 * `status: 'disabled'` and all actions are no-ops, so the desktop and
 * local-only web experiences are unaffected and never gated by a login screen.
 */

import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import type { Session, User } from '@supabase/supabase-js'
import { getSupabase, isSupabaseConfigured } from '../runtime/supabase'
import { verifyAdminUserAccess } from './authorization'

export type AuthStatus = 'disabled' | 'loading' | 'signedOut' | 'signedIn'

interface AuthActionResult {
  error: string | null
}

function hasRecoveryTypeInUrl(): boolean {
  const searchParams = new URLSearchParams(window.location.search)
  if (searchParams.get('type') === 'recovery') return true

  const hash = window.location.hash.startsWith('#')
    ? window.location.hash.slice(1)
    : window.location.hash
  const hashParams = new URLSearchParams(hash)
  return hashParams.get('type') === 'recovery'
}

export interface AuthContextValue {
  status: AuthStatus
  session: Session | null
  user: User | null
  shouldResetPassword: boolean
  signInWithPassword: (email: string, password: string) => Promise<AuthActionResult>
  signUp: (email: string, password: string) => Promise<AuthActionResult>
  signInWithMagicLink: (email: string) => Promise<AuthActionResult>
  requestPasswordReset: (email: string) => Promise<AuthActionResult>
  updatePassword: (password: string) => Promise<AuthActionResult>
  verifyAdminAccess: () => Promise<boolean>
  signOut: () => Promise<void>
}

const noopResult: AuthActionResult = { error: null }

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const supabase = getSupabase()
  const [session, setSession] = useState<Session | null>(null)
  const [status, setStatus] = useState<AuthStatus>(isSupabaseConfigured ? 'loading' : 'disabled')
  const [shouldResetPassword, setShouldResetPassword] = useState<boolean>(() =>
    isSupabaseConfigured ? hasRecoveryTypeInUrl() : false
  )

  useEffect(() => {
    if (!supabase) return
    let active = true

    void supabase.auth.getSession().then(({ data }) => {
      if (!active) return
      setSession(data.session)
      setStatus(data.session ? 'signedIn' : 'signedOut')
    })

    const { data: sub } = supabase.auth.onAuthStateChange((event, next) => {
      setSession(next)
      setStatus(next ? 'signedIn' : 'signedOut')
      if (event === 'PASSWORD_RECOVERY') {
        setShouldResetPassword(true)
      } else if (event === 'SIGNED_OUT') {
        setShouldResetPassword(false)
      }
    })

    return () => {
      active = false
      sub.subscription.unsubscribe()
    }
  }, [supabase])

  const value = useMemo<AuthContextValue>(() => {
    if (!supabase) {
      return {
        status: 'disabled',
        session: null,
        user: null,
        shouldResetPassword: false,
        signInWithPassword: () => Promise.resolve(noopResult),
        signUp: () => Promise.resolve(noopResult),
        signInWithMagicLink: () => Promise.resolve(noopResult),
        requestPasswordReset: () => Promise.resolve(noopResult),
        updatePassword: () => Promise.resolve(noopResult),
        verifyAdminAccess: () => Promise.resolve(false),
        signOut: () => Promise.resolve()
      }
    }

    return {
      status,
      session,
      user: session?.user ?? null,
      shouldResetPassword,
      signInWithPassword: async (email, password) => {
        const { error } = await supabase.auth.signInWithPassword({ email, password })
        return { error: error?.message ?? null }
      },
      signUp: async (email, password) => {
        const { error } = await supabase.auth.signUp({ email, password })
        return { error: error?.message ?? null }
      },
      signInWithMagicLink: async (email) => {
        const { error } = await supabase.auth.signInWithOtp({
          email,
          options: { emailRedirectTo: window.location.origin }
        })
        return { error: error?.message ?? null }
      },
      requestPasswordReset: async (email) => {
        const { error } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: window.location.origin
        })
        return { error: error?.message ?? null }
      },
      updatePassword: async (password) => {
        const { error } = await supabase.auth.updateUser({ password })
        if (!error) {
          setShouldResetPassword(false)
        }
        return { error: error?.message ?? null }
      },
      verifyAdminAccess: () => verifyAdminUserAccess(supabase.auth),
      signOut: async () => {
        await supabase.auth.signOut()
      }
    }
  }, [supabase, status, session, shouldResetPassword])

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return ctx
}
